import type { WeightResponse, WeightTrendPoint } from '@nutians/shared/schemas';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../api.js';

export const WEIGHT_KEY = ['weight'] as const;

function currentTimezone(): string | undefined {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || undefined;
  } catch {
    return undefined;
  }
}

function localDateOf(date: Date, timezone: string | undefined): string {
  if (timezone) {
    try {
      return new Intl.DateTimeFormat('en-CA', { timeZone: timezone }).format(date);
    } catch {
      // fall through to UTC
    }
  }
  return date.toISOString().slice(0, 10);
}

function optimisticTrend(points: WeightTrendPoint[], scaleKg: number): number {
  const previous = points.at(-1)?.trendKg ?? scaleKg;
  return Math.round((0.1 * scaleKg + 0.9 * previous) * 10) / 10;
}

export function useWeightRange(days = 84) {
  const timezone = currentTimezone();
  const toDate = new Date();
  const fromDate = new Date(toDate);
  fromDate.setUTCDate(fromDate.getUTCDate() - (days - 1));
  const from = fromDate.toISOString().slice(0, 10);
  const to = toDate.toISOString().slice(0, 10);

  return useQuery<WeightResponse>({
    queryKey: [...WEIGHT_KEY, from, to, timezone],
    queryFn: async (): Promise<WeightResponse> => {
      const { data, error } = await api.GET('/me/weight', {
        params: { query: { from, to, timezone } },
      });
      if (error || !data) throw new Error('GET /me/weight failed');
      return data as WeightResponse;
    },
    staleTime: 60_000,
    retry: false,
  });
}

export function useCreateWeight() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ weightKg }: { weightKg: number }) => {
      const measuredAt = new Date();
      const timezone = currentTimezone();
      const { data, error } = await api.POST('/me/weight', {
        body: { measuredAt: measuredAt.toISOString(), weightKg, timezone },
      });
      if (error || !data) throw new Error('POST /me/weight failed');
      return data as WeightTrendPoint;
    },
    onMutate: async ({ weightKg }) => {
      await qc.cancelQueries({ queryKey: WEIGHT_KEY });
      const snapshots = qc.getQueriesData<WeightResponse>({ queryKey: WEIGHT_KEY });
      const measuredAt = new Date();
      const timezone = currentTimezone();

      for (const [queryKey, current] of snapshots) {
        if (!current) continue;
        const optimistic: WeightTrendPoint = {
          id: `00000000-0000-4000-8000-${String(Date.now()).slice(-12).padStart(12, '0')}`,
          localDate: localDateOf(measuredAt, timezone),
          measuredAt: measuredAt.toISOString(),
          scaleKg: weightKg,
          trendKg: optimisticTrend(current.points, weightKg),
          source: 'manual',
        };
        qc.setQueryData<WeightResponse>(queryKey, {
          points: [
            ...current.points.filter((point) => point.localDate !== optimistic.localDate),
            optimistic,
          ],
        });
      }

      return { snapshots };
    },
    onError: (_error, _variables, context) => {
      for (const [queryKey, data] of context?.snapshots ?? []) {
        qc.setQueryData(queryKey, data);
      }
    },
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: WEIGHT_KEY });
    },
  });
}
